import type { MetaDescriptor } from 'react-router';
import type { VideoObject } from 'schema-dts';
import { resolveVideoUrls } from '../util/resolve-video-urls';

export interface VideoSchemaProps {
    /** Video title. */
    name: string;
    /** Plain-text summary of the video. */
    description?: string | null;
    /** Link the video is played from: a YouTube/Vimeo link, or a direct media file. */
    url?: string | null;
    /** Absolute thumbnail URL. */
    thumbnailUrl?: string | null;
    /** Date the video was published, as `YYYY-MM-DD` or any string starting with that prefix. */
    publishDate?: string | null;
    /** Professional who published the video; becomes the work's author. */
    professionalName?: string | null;
}

const CALENDAR_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

/**
 * Reduce a stored date to a bare `YYYY-MM-DD` calendar date, or nothing when it cannot be read.
 *
 * Dates reach us as `YYYY-MM-DD`, MySQL `YYYY-MM-DD HH:mm:ss`, or ISO-8601, all of which start with the
 * calendar date. Taking the prefix rather than parsing avoids `Date` applying the local timezone, which
 * would render a different day on a UTC server than in an NZ/AU browser and break hydration of the
 * `ld+json` script. Anything else is discarded: an unreadable `uploadDate` invalidates the whole video
 * for Google, so emitting nothing is better than emitting a guess.
 */
const toCalendarDate = (value: string | null | undefined): string | undefined =>
    CALENDAR_DATE_PREFIX.exec(value?.trim() ?? '')?.[1];

/**
 * Build VideoObject JSON-LD for a single video.
 *
 * Google requires `name`, `thumbnailUrl`, `uploadDate` and one of `contentUrl` / `embedUrl` before a
 * video is eligible for video rich results and the Videos tab; a video missing any of them is reported
 * as an error in Search Console rather than ignored. This returns an empty descriptor list in that case
 * so callers can map over every video on a page and let the unusable ones drop out.
 */
export const createVideoSchema = (
    props: VideoSchemaProps,
): MetaDescriptor[] => {
    const {
        name,
        description,
        url,
        thumbnailUrl,
        publishDate,
        professionalName,
    } = props;

    const title = name?.trim();
    const thumbnail = thumbnailUrl?.trim();
    const uploadDate = toCalendarDate(publishDate);
    const { embedUrl, contentUrl } = resolveVideoUrls(url);

    if (!title || !thumbnail || !uploadDate || !(embedUrl || contentUrl)) {
        return [];
    }

    const videoSchema: VideoObject = {
        '@type': 'VideoObject',
        '@id': embedUrl ?? contentUrl,
        name: title,
        uploadDate,
        datePublished: uploadDate,
        thumbnailUrl: [thumbnail],
        ...(description?.trim() ? { description: description.trim() } : {}),
        ...(embedUrl ? { embedUrl } : {}),
        ...(contentUrl ? { contentUrl } : {}),
        ...(professionalName?.trim()
            ? {
                  author: {
                      '@type': 'Organization',
                      name: professionalName.trim(),
                  },
              }
            : {}),
        publisher: {
            '@type': 'Organization',
            name: 'ArchiPro',
        },
    };

    return [videoSchema];
};
